import React from "react";
import PropTypes from "prop-types";

import EntitySubtitle from "views_shared/app/javascript/components/EntitySubtitle";
// Aliased path, not a relative one: V1 maps ImageCropped onto its own React 16 copy, and a
// relative import would drag the React 18 copy into a React 16 tree.
import ImageCropped from "views_shared/app/javascript/components/ImageCropped";
import { expertUrl } from "views_shared/app/javascript/utils/urls";

// The pitch's proposed source, repeated in the conversation header ("Add the proposed source
// in the header as well please"). Desktop gets the bordered chip from the design; tablet and
// mobile get the same facts on one line, which is all that fits beside the name.
function ProposedSourceChip({ entity, moreCount = 0, compact = false }) {
  const profileHref = expertUrl({ slug: entity.slug, migrate_id: entity.migrate_id });
  // Only one source fits beside the name, so a multi-source pitch (0.01% of them, never more
  // than two) is headlined and counted here; the full list is in the message body below.
  const plural = moreCount > 0 ? "s" : "";
  // Two spellings, swapped by the header's width: at ~390px "Proposed Sources:" alone eats the
  // row and leaves the name a few pixels.
  const label = (
    <>
      <span className="csc-label-long">{`Proposed Source${plural}:`}</span>
      <span className="csc-label-short">{`Source${plural}:`}</span>
    </>
  );
  const more = moreCount > 0 ? <span className="csc-more flex-shrink-0">{`+${moreCount}`}</span> : null;
  const viewProfile = (
    <a href={profileHref} target="_blank" rel="noopener noreferrer" className="csc-view-profile flex-shrink-0">
      (view profile)
    </a>
  );

  if (compact) {
    // One line beside the name leaves no room for a separate "(view profile)" — at 390px the
    // label, the link and the count together push the name out entirely. The name carries the
    // link instead, so the affordance survives and the name is what gets the space.
    return (
      <p className="csc-compact paragraph-sm text-secondary mb-0 d-flex align-items-baseline gap-1">
        <span className="csc-label fw-bold flex-shrink-0">{label}</span>
        <a
          href={profileHref}
          target="_blank"
          rel="noopener noreferrer"
          className="csc-view-profile text-truncate"
          style={{ minWidth: 0 }}
        >
          {entity.name}
        </a>
        {more}
      </p>
    );
  }

  return (
    <div className="csc-chip d-flex align-items-center">
      <span className="csc-label fw-bold flex-shrink-0">{label}</span>
      {entity.image_url && (
        <ImageCropped roundedCircle src={entity.image_url} alt={entity.name} size={34} fallbackToInitials />
      )}
      <div style={{ minWidth: 0 }}>
        <div className="d-flex align-items-baseline gap-1">
          <span className="fw-bold text-truncate">{entity.name}</span>
          {viewProfile}
          {more}
        </div>
        {(entity.title || entity.company_name) && (
          <p className="paragraph-sm text-secondary mb-0 text-truncate">
            <EntitySubtitle entity={entity} linkClassName="csc-company-link" />
          </p>
        )}
      </div>
    </div>
  );
}

ProposedSourceChip.propTypes = {
  entity: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    migrate_id: PropTypes.string,
    image_url: PropTypes.string,
    title: PropTypes.string,
    company_name: PropTypes.string,
  }).isRequired,
  moreCount: PropTypes.number,
  compact: PropTypes.bool,
};

export default ProposedSourceChip;
